import { makeStyles, Tab, TabList, tokens, type SelectTabData, type SelectTabEvent } from '@fluentui/react-components';
import React, { useState } from 'react';
import useConfigStore from '../../store/config';
import ProjektRechnungKontoTab from './AccountsTab';
import FoerdergeberTab from './FoerdergeberTab';
import HeadOfInstitutesTab from './HeadOfInstitutesTab';
import HourlyRatesTab from './HourlyRatesTab';
import InformationGroupsTab from './InformationGroupsTab';
import KeyResearchAreaTab from './KeyResearchAreaTab';
import RoleManagementTab from './RoleManagementTab';
import SourceOfFundsTab from './SourceOfFundsTab';

// Tab configuration with role-based access
interface TabConfig {
    id: string;
    label: string;
    allowedRoles: string[];
}

const TAB_CONFIG: TabConfig[] = [
    { id: 'role-management', label: 'Role Management', allowedRoles: ['Administratoren'] },
    { id: 'head-of-institutes', label: 'Head of Institutes', allowedRoles: ['Administratoren'] },
    { id: 'information-groups', label: 'Information Groups', allowedRoles: ['Administratoren'] },
    { id: 'key-research-area', label: 'Key Research Area', allowedRoles: ['Administratoren', 'Forschungsservice'] },
    { id: 'source-of-funds', label: 'Source of Funds', allowedRoles: ['Administratoren'] },
    { id: 'projekt-rechnung-konto', label: 'Accounts', allowedRoles: ['Administratoren'] },
    { id: 'foerdergeber', label: 'Funding agencies', allowedRoles: ['Administratoren'] },
    { id: 'hourly-rates', label: 'Hourly Rates', allowedRoles: ['Administratoren'] },
];

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    tabsContainer: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: tokens.spacingVerticalL,
    },
    content: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    unauthorizedContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: tokens.spacingVerticalL,
        padding: tokens.spacingHorizontalL,
    },
    unauthorizedText: {
        fontSize: tokens.fontSizeBase500,
        color: tokens.colorNeutralForeground1,
        textAlign: 'center',
    },
});

// Allowed roles for Settings access
const ALLOWED_ROLES = ['Administratoren', 'Forschungsservice'];

const Settings: React.FC = () => {
    const styles = useStyles();
    const selectedRole = useConfigStore((state) => state.roleConfig.selectedRole);
    const [selectedTab, setSelectedTab] = useState<string>('');

    // Filter available tabs based on user role (memoized to avoid recalculation)
    const availableTabs = React.useMemo(() => {
        return TAB_CONFIG.filter((tab) => {
            if (!selectedRole) return false;
            return tab.allowedRoles.includes(selectedRole);
        });
    }, [selectedRole]);

    // Compute effective tab to display
    // If selectedTab is not in availableTabs or empty, use first available tab
    const effectiveTab = React.useMemo(() => {
        if (availableTabs.length === 0) return '';
        const isSelected = availableTabs.some((tab) => tab.id === selectedTab);
        return isSelected ? selectedTab : availableTabs[0].id;
    }, [selectedTab, availableTabs]);

    // Check if user has permission to access Settings
    const hasPermission = selectedRole && ALLOWED_ROLES.includes(selectedRole);

    if (!hasPermission) {
        return (
            <div className={styles.unauthorizedContainer}>
                <div className={styles.unauthorizedText}>
                    You do not have permission to access Settings.
                </div>
                <div style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 }}>
                    Only {ALLOWED_ROLES.join(' and ')} roles can access this section.
                </div>
            </div>
        );
    }

    const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
        setSelectedTab(data.value as string);
    };

    const renderTabContent = () => {
        switch (effectiveTab) {
            case 'role-management':
                return <RoleManagementTab />;
            case 'head-of-institutes':
                return <HeadOfInstitutesTab />;
            case 'information-groups':
                return <InformationGroupsTab />;
            case 'key-research-area':
                return <KeyResearchAreaTab />;
            case 'source-of-funds':
                return <SourceOfFundsTab />;
            case 'projekt-rechnung-konto':
                return <ProjektRechnungKontoTab />;
            case 'foerdergeber':
                return <FoerdergeberTab />;
            case 'hourly-rates':
                return <HourlyRatesTab />;
            default:
                return availableTabs.length > 0 ? null : <div>No tabs available for your role. Please choose a privileged role or contact the administrator.</div>;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.tabsContainer}>
                <TabList selectedValue={effectiveTab} onTabSelect={onTabSelect}>
                    {availableTabs.map((tab) => (
                        <Tab key={tab.id} value={tab.id}>
                            {tab.label}
                        </Tab>
                    ))}
                </TabList>
            </div>
            <div className={styles.content}>
                {renderTabContent()}
            </div>
        </div>
    );
};

export default Settings;
